import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Users, Zap, Ruler, Droplets, ShieldCheck, ChevronRight, ChevronLeft, Check, Palette } from 'lucide-react';
import { CONTACT } from '../data/constants';
import { PDC_SHELL_COLORS, PDC_CABINET_COLORS } from '../data/pdcSpas';
import { PDC_SPECS } from '../data/pdcSpecs';

export const phoneHref = `tel:${CONTACT.phone.replace(/[^0-9]/g, '')}`;

// ---------- Color Swatch ----------
const Swatch = ({ color, selected, onClick, testid }) => (
  <motion.button
    type="button"
    onClick={onClick}
    whileHover={{ scale: 1.08 }}
    whileTap={{ scale: 0.95 }}
    className="relative group flex flex-col items-center w-[70px]"
    aria-label={`Select ${color.name}`}
    data-testid={testid}
  >
    <div className={`w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${
      selected ? 'border-[#F88200] ring-2 ring-[#F88200] ring-offset-2' : 'border-slate-200 hover:border-slate-400'
    }`}>
      {color.image ? (
        <img
          src={color.image}
          alt={color.name}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.style.backgroundColor = color.hex; }}
        />
      ) : (
        <div className="w-full h-full" style={{ backgroundColor: color.hex }} />
      )}
      {selected && (
        <div className="absolute inset-0 bg-[#F88200]/20 flex items-center justify-center">
          <Check className="text-[#F88200]" size={22} strokeWidth={3} />
        </div>
      )}
    </div>
    <p className={`text-[11px] mt-1 text-center font-medium leading-tight ${selected ? 'text-[#F88200]' : 'text-slate-600'}`}>
      {color.name}
    </p>
  </motion.button>
);

// ---------- Interactive Color Picker (controlled) ----------
const ColorPicker = ({ product, shell, cabinet, onShell, onCabinet }) => {
  const shellKeys = product.shellColors || [];
  const cabinetKeys = product.cabinetColors || [];

  const shellColor = PDC_SHELL_COLORS[shell];
  const cabinetColor = PDC_CABINET_COLORS[cabinet];

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mb-6" data-testid="pdc-color-picker">
      <div className="flex items-center gap-2 mb-4">
        <Palette size={18} className="text-[#F88200]" />
        <h4 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-[#0E3D95]">Build Your Colors</h4>
      </div>

      {/* Big live preview of the two selected swatches */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="rounded-lg overflow-hidden border border-slate-200 bg-white">
          <div className="h-24 w-full" style={{ backgroundColor: shellColor?.hex }}>
            {shellColor?.image && (
              <img src={shellColor.image} alt={shellColor.name} className="w-full h-full object-cover" loading="lazy"
                onError={(e) => { e.target.style.display = 'none'; }} />
            )}
          </div>
          <div className="px-3 py-2">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Shell</p>
            <p className="text-sm font-bold text-[#0E3D95]">{shellColor?.name}</p>
          </div>
        </div>
        <div className="rounded-lg overflow-hidden border border-slate-200 bg-white">
          <div className="h-24 w-full" style={{ backgroundColor: cabinetColor?.hex }}>
            {cabinetColor?.image && (
              <img src={cabinetColor.image} alt={cabinetColor.name} className="w-full h-full object-cover" loading="lazy"
                onError={(e) => { e.target.style.display = 'none'; }} />
            )}
          </div>
          <div className="px-3 py-2">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Cabinet</p>
            <p className="text-sm font-bold text-[#0E3D95]">{cabinetColor?.name}</p>
          </div>
        </div>
      </div>

      {/* Shell swatches */}
      {shellKeys.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
            {shellKeys.length > 1 ? 'Acrylic Shell Color' : 'Signature Shell'}
          </p>
          <div className="flex flex-wrap gap-2">
            {shellKeys.map((key) => (
              PDC_SHELL_COLORS[key] && (
                <Swatch key={key} color={PDC_SHELL_COLORS[key]} selected={shell === key}
                  onClick={() => onShell(key)} testid={`pdc-shell-${key}`} />
              )
            ))}
          </div>
        </div>
      )}

      {/* Cabinet swatches */}
      {cabinetKeys.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Cabinet Finish</p>
          <div className="flex flex-wrap gap-2">
            {cabinetKeys.map((key) => (
              PDC_CABINET_COLORS[key] && (
                <Swatch key={key} color={PDC_CABINET_COLORS[key]} selected={cabinet === key}
                  onClick={() => onCabinet(key)} testid={`pdc-cabinet-${key}`} />
              )
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-slate-400 italic mt-4">
        Swatches are approximate — actual finishes may vary. Ask us for physical samples.
      </p>
    </div>
  );
};

// ---------- Spec chip ----------
const SpecChip = ({ icon: Icon, label, value, wide }) => (
  <div className={`bg-white border border-slate-200 rounded-lg p-3 ${wide ? 'col-span-2' : ''}`}>
    <div className="flex items-center gap-2 text-xs text-slate-500 uppercase font-bold mb-1">
      <Icon size={14} className="text-[#F88200]" /> {label}
    </div>
    <p className="font-bold text-[#0E3D95] text-sm">{value}</p>
  </div>
);

// ---------- Full Product Detail (used on the dedicated product page) ----------
export const PDCProductDetail = ({ product }) => {
  const images = product.images || [];
  const [shell, setShell] = useState(product.standardShell || (product.shellColors || [])[0]);
  const [cabinet, setCabinet] = useState((product.cabinetColors || [])[0]);
  const [hero, setHero] = useState({ type: 'product', idx: 0 });

  const detail = PDC_SPECS[product.id] || {};
  const specRows = detail.specs || [];
  const included = (detail.included && detail.included.length ? detail.included : product.features) || [];
  const options = detail.options || [];

  const showProduct = (i) => setHero({ type: 'product', idx: i });
  const next = () => setHero((h) => ({ type: 'product', idx: ((h.type === 'product' ? h.idx : 0) + 1) % images.length }));
  const prev = () => setHero((h) => ({ type: 'product', idx: ((h.type === 'product' ? h.idx : 0) - 1 + images.length) % images.length }));
  const chooseShell = (key) => { setShell(key); setHero({ type: 'shell', key }); };
  const chooseCabinet = (key) => { setCabinet(key); setHero({ type: 'cabinet', key }); };

  let heroSrc = images[0];
  let heroHex = null;
  let heroCaption = product.name;
  if (hero.type === 'product') {
    heroSrc = images[hero.idx]; heroCaption = product.name;
  } else if (hero.type === 'shell') {
    const c = PDC_SHELL_COLORS[hero.key];
    heroSrc = c?.image || null; heroHex = c?.hex; heroCaption = `${c?.name} Shell`;
  } else if (hero.type === 'cabinet') {
    const c = PDC_CABINET_COLORS[hero.key];
    heroSrc = c?.image || null; heroHex = c?.hex; heroCaption = `${c?.name} Cabinet`;
  }
  const productActive = hero.type === 'product';

  return (
    <div className="grid lg:grid-cols-2 gap-8 lg:gap-12" data-testid="pdc-product-detail">
      {/* Gallery */}
      <div className="lg:sticky lg:top-28 lg:self-start">
        <div className="relative bg-white rounded-2xl overflow-hidden aspect-square shadow-lg border border-slate-100">
          {heroSrc ? (
            <img src={heroSrc} alt={heroCaption} className="w-full h-full object-contain p-4" />
          ) : (
            <div className="w-full h-full" style={{ backgroundColor: heroHex || '#e2e8f0' }} />
          )}
          <div className="absolute bottom-3 left-3 bg-black/60 text-white text-xs font-semibold px-3 py-1.5 rounded pointer-events-none" data-testid="pdc-hero-caption">
            {heroCaption}
          </div>
          {images.length > 1 && (
            <>
              <button onClick={prev} className="absolute top-1/2 left-3 -translate-y-1/2 w-11 h-11 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center" aria-label="Previous image">
                <ChevronLeft size={24} />
              </button>
              <button onClick={next} className="absolute top-1/2 right-3 -translate-y-1/2 w-11 h-11 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center" aria-label="Next image">
                <ChevronRight size={24} />
              </button>
            </>
          )}
        </div>
        {/* Thumbnail strip */}
        <div className="flex gap-2 mt-3 flex-wrap">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => showProduct(i)}
              className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                productActive && hero.idx === i ? 'border-[#F88200] ring-1 ring-[#F88200]' : 'border-slate-200 hover:border-slate-400'
              }`}
              aria-label={`Show ${product.name} photo ${i + 1}`}
              data-testid={`pdc-hero-thumb-${i}`}
            >
              <img src={img} alt={`${product.name} thumbnail ${i + 1}`} className="w-full h-full object-contain bg-white" loading="lazy" />
            </button>
          ))}
        </div>
      </div>

      {/* Info */}
      <div>
        <p className="text-sm uppercase tracking-widest text-[#F88200] font-bold mb-1">PDC Spas · {product.series}</p>
        <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-black uppercase text-[#0E3D95] leading-none mb-3">{product.name}</h1>
        <p className="text-slate-700 leading-relaxed mb-5">{product.description}</p>

        {/* Quick specs */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <SpecChip icon={Users} label={product.category === 'swim-spas' ? 'Size' : 'Seats'} value={product.seats} />
          {product.jets > 0 && <SpecChip icon={Zap} label="Jets" value={product.jets} />}
          {product.gallons && <SpecChip icon={Droplets} label="Water" value={`${product.gallons} gal`} />}
          <SpecChip icon={ShieldCheck} label="Warranty" value={product.warranty} />
          {product.dimensions && <SpecChip icon={Ruler} label="Dimensions" value={product.dimensions} wide />}
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <a href={phoneHref} className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="pdc-spa-call-btn">
            <Phone size={18} /> Call for Pricing · {CONTACT.phone}
          </a>
          <Link to="/contact" className="btn-secondary flex-1 flex items-center justify-center gap-2">
            Request a Quote
          </Link>
        </div>

        {/* Interactive color picker */}
        <ColorPicker product={product} shell={shell} cabinet={cabinet} onShell={chooseShell} onCabinet={chooseCabinet} />

        {/* Full Specifications table (as shown on pdcspas.com) */}
        {specRows.length > 0 && (
          <div className="mb-6">
            <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-[#0E3D95] mb-3">Specifications</h3>
            <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="pdc-spec-table">
              {specRows.map(([label, value], i) => (
                <div key={i} className={`flex flex-wrap gap-x-3 px-4 py-2.5 text-sm ${i % 2 === 0 ? 'bg-slate-50' : 'bg-white'}`}>
                  <div className="w-full sm:w-2/5 font-semibold text-[#0E3D95]">{label}</div>
                  <div className="w-full sm:w-3/5 text-slate-700">{value}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Features Included */}
        {included.length > 0 && (
          <div className="mb-6">
            <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-[#0E3D95] mb-3">Features Included</h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              {included.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <Check size={16} className="text-[#F88200] mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Exclusive Options */}
        {options.length > 0 && (
          <div className="mb-2">
            <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-[#0E3D95] mb-3">Exclusive Options</h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              {options.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <span className="text-[#F88200] font-bold mt-0.5 flex-shrink-0">+</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Product Card (links to its own page) ----------
export const PDCProductCard = ({ product }) => {
  const shellCount = (product.shellColors || []).length;
  const cabinetCount = (product.cabinetColors || []).length;
  return (
    <motion.div whileHover={{ y: -4 }} className="h-full">
      <Link
        to={`/pdc-spas/${product.id}`}
        className="relative bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow text-left group flex flex-col w-full h-full"
        data-testid={`pdc-spa-card-${product.id}`}
      >
        <div className="relative aspect-[4/3] bg-white overflow-hidden">
          <img src={product.images[0]} alt={product.name} className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-500" loading="lazy" />
          <div className="absolute top-2 left-2 bg-[#0E3D95] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded">
            {product.series}
          </div>
          <div className="absolute top-2 right-2 bg-[#F88200] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded">
            Call for Pricing
          </div>
        </div>
        <div className="p-4 flex flex-col flex-1">
          <h3 className="font-['Barlow_Condensed'] text-xl md:text-2xl font-bold uppercase text-[#0E3D95] leading-none mb-1">{product.name}</h3>
          <p className="text-xs text-slate-500 mb-3 line-clamp-2 flex-1">{product.description}</p>
          <div className="flex items-center gap-3 text-[11px] text-slate-500 mb-3">
            <span className="inline-flex items-center gap-1"><Users size={12} /> {product.seats}</span>
            {product.jets > 0 && <span className="inline-flex items-center gap-1"><Zap size={12} /> {product.jets} jets</span>}
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 font-medium">
              <Palette size={13} className="text-[#F88200]" /> {shellCount + cabinetCount} color options
            </span>
            <span className="inline-flex items-center gap-1 text-xs font-bold text-[#0E3D95] group-hover:text-[#F88200] uppercase tracking-wider">
              View <ChevronRight size={14} />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
